import { useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Award, 
  Wrench, 
  Users, 
  Star, 
  CheckCircle,
  Shield,
  Heart,
  Calendar,
  BookOpen,
  Home,
  GraduationCap,
  Trophy,
  Building2,
  Zap,
  ClipboardList,
  Target,
  Clock
} from 'lucide-react';

const CodeeJessen = () => {
  // SEO meta tags setup
  useEffect(() => {
    
    const metaTags = [
      { name: 'description', content: 'Meet Codee Jessen, Licensed Colorado Master Plumber and Owner of Colorado First Plumbing. Over 10 multifamily projects, 6000+ customers served, expert in DWV, water, gas, and IPC code compliance.' },
      { name: 'keywords', content: 'Codee Jessen master plumber, Colorado master plumber license, multifamily plumbing expert, underground plumbing specialist, IPC code compliance, Denver plumber owner' },
      { property: 'og:title', content: 'Codee Jessen - Master Plumber & Owner | Colorado First Plumbing' },
      { property: 'og:description', content: 'Licensed Colorado Master Plumber with expertise in multifamily residential, underground layout, and comprehensive plumbing services.' },
      { property: 'og:type', content: 'profile' },
      { property: 'og:url', content: 'https://colorado-plumbing.com/team/codee-jessen' },
    ];
    
    metaTags.forEach(tag => {
      let metaElement = document.querySelector(`meta[${tag.name ? 'name' : 'property'}="${tag.name || tag.property}"]`);
      if (metaElement) {
        metaElement.setAttribute('content', tag.content);
      } else {
        metaElement = document.createElement('meta');
        if (tag.name) {
          metaElement.setAttribute('name', tag.name);
        } else {
          metaElement.setAttribute('property', tag.property);
        }
        metaElement.setAttribute('content', tag.content);
        document.head.appendChild(metaElement);
      }
    });

    // Structured data for Codee Jessen
    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'Person',
      'name': 'Codee Jessen',
      'jobTitle': 'Master Plumber & Owner',
      'worksFor': {
        '@type': 'LocalBusiness',
        'name': 'Colorado First Plumbing and Sewer LLC',
        'url': 'https://colorado-plumbing.com'
      },
      'description': 'Licensed Colorado Master Plumber with over 10 multifamily projects completed and 6000+ customers served since 2018.',
      'knowsAbout': ['Multifamily Residential Plumbing', 'Underground Layout', 'DWV Systems', 'Water Systems', 'Gas Systems', 'IPC Code Compliance'],
      'award': 'Licensed Colorado Master Plumber',
      'url': 'https://colorado-plumbing.com/team/codee-jessen'
    };

    let structuredDataScript = document.querySelector('#codee-structured-data');
    if (structuredDataScript) {
      structuredDataScript.textContent = JSON.stringify(structuredData);
    } else {
      structuredDataScript = document.createElement('script');
      structuredDataScript.setAttribute('id', 'codee-structured-data');
      structuredDataScript.setAttribute('type', 'application/ld+json');
      structuredDataScript.textContent = JSON.stringify(structuredData);
      document.head.appendChild(structuredDataScript);
    }

    return () => {
      const script = document.querySelector('#codee-structured-data');
      if (script) {
        document.head.removeChild(script);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-light to-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-60 pb-16 bg-gradient-to-br from-trust-deep to-authority-navy text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-trust-primary/20 to-action-primary/20"></div>
        <div className="container-custom relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center mb-6">
                <Award className="w-8 h-8 text-action-warm mr-3" />
                <span className="px-4 py-2 bg-action-primary/20 rounded-full text-action-warm font-medium text-sm border border-action-primary/30">
                  Licensed Master Plumber
                </span>
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight">
                Codee Jessen
              </h1>
              <h2 className="text-2xl md:text-3xl text-action-warm font-semibold mb-6">
                Master Plumber & Owner
              </h2>
              
              <p className="text-xl text-trust-light mb-8 leading-relaxed">
                Licensed Colorado Master Plumber with extensive experience in multifamily residential projects, 
                underground layout specialization, and comprehensive home service expertise serving the Denver Metro area since 2018.
              </p>
              
              {/* Key Stats */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="bg-white/10 backdrop-blur-md rounded-lg p-4 border border-white/20">
                  <div className="text-2xl font-bold text-action-warm">6000+</div>
                  <div className="text-sm text-trust-light">Customers Served</div>
                </div>
                <div className="bg-white/10 backdrop-blur-md rounded-lg p-4 border border-white/20">
                  <div className="text-2xl font-bold text-success-eco">10+</div>
                  <div className="text-sm text-trust-light">Multifamily Projects</div>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                
                <a 
                  href="#experience" 
                  className="inline-flex items-center px-6 py-3 rounded-full bg-white/10 backdrop-blur-md text-white hover:bg-white/20 transition-all duration-300 font-medium border border-white/30"
                >
                  <BookOpen size={16} className="mr-2" />
                  View Experience
                </a>
              </div>
            </div>
            
            {/* Team Photos */}
            <div className="grid grid-cols-1 gap-4">
              <div className="aspect-[4/3] rounded-xl overflow-hidden shadow-2xl border-4 border-white/30">
                <img 
                  src="/colorado-plumbing-uploads/team/codee-jessen.png" 
                  alt="Codee Jessen - Master Plumber & Owner at Colorado First Plumbing" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="aspect-[4/3] rounded-xl overflow-hidden shadow-2xl border-4 border-white/30">
                <img 
                  src="/colorado-plumbing-uploads/team/codee-jessen-coach.png" 
                  alt="Codee Jessen - Soccer Coach and Community Volunteer" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Professional Experience Section */}
      <section id="experience" className="py-16 bg-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-authority-navy mb-4">
              Professional Experience & Expertise
            </h2>
            <p className="text-xl text-neutral-gray max-w-3xl mx-auto">
              Comprehensive plumbing expertise from large-scale multifamily projects to residential home services throughout Colorado.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Current Role */}
            <div className="bg-trust-light rounded-xl p-8 border border-trust-primary/20">
              <div className="flex items-center mb-6">
                <Building2 className="w-8 h-8 text-trust-primary mr-3" />
                <div>
                  <h3 className="text-xl font-bold text-authority-navy">Current Role</h3>
                  <p className="text-trust-primary font-medium">2018 - Present</p>
                </div>
              </div>
              
              <h4 className="text-lg font-bold text-authority-navy mb-4">Owner & Master Plumber</h4>
              <p className="text-neutral-gray mb-4">Colorado First Plumbing and Sewer LLC</p>
              
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-success-primary mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-neutral-gray">6000+ customers serviced across Denver Metro area</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-success-primary mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-neutral-gray">300+ permitted sewer replacement projects completed</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-success-primary mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-neutral-gray">1000's of Colorado home service jobs completed</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-success-primary mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-neutral-gray">Business growth from startup to established Denver Metro leader</span>
                </li>
              </ul>
            </div>

            {/* Previous Experience */}
            <div className="space-y-6">
              {/* Freund Plumbing */}
              <div className="bg-gradient-to-br from-neutral-light to-white rounded-xl p-6 border border-neutral-border shadow-md">
                <div className="flex items-center mb-4">
                  <Target className="w-6 h-6 text-action-primary mr-3" />
                  <div>
                    <h4 className="text-lg font-bold text-authority-navy">Freund Plumbing</h4>
                    <p className="text-action-primary font-medium">Superintendent Role</p>
                  </div>
                </div>
                
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-action-primary rounded-full mr-3 mt-2 flex-shrink-0"></span>
                    <span className="text-sm text-neutral-gray">Underground Layout Specialist</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-action-primary rounded-full mr-3 mt-2 flex-shrink-0"></span>
                    <span className="text-sm text-neutral-gray">Multiple Site Management (50-400 units)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-action-primary rounded-full mr-3 mt-2 flex-shrink-0"></span>
                    <span className="text-sm text-neutral-gray">Employee and Piece Work Management</span>
                  </li>
                </ul>
              </div>

              {/* Utah Mechanical */}
              <div className="bg-gradient-to-br from-neutral-light to-white rounded-xl p-6 border border-neutral-border shadow-md">
                <div className="flex items-center mb-4">
                  <ClipboardList className="w-6 h-6 text-trust-primary mr-3" />
                  <div>
                    <h4 className="text-lg font-bold text-authority-navy">Utah Mechanical</h4>
                    <p className="text-trust-primary font-medium">Foreman</p>
                  </div>
                </div>
                
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-trust-primary rounded-full mr-3 mt-2 flex-shrink-0"></span>
                    <span className="text-sm text-neutral-gray">350+ Unit Site Management</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-trust-primary rounded-full mr-3 mt-2 flex-shrink-0"></span>
                    <span className="text-sm text-neutral-gray">Material Ordering & Coordination</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-trust-primary rounded-full mr-3 mt-2 flex-shrink-0"></span>
                    <span className="text-sm text-neutral-gray">Rough and Finish Layout</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technical Expertise Section */}
      <section className="py-16 bg-gradient-to-br from-neutral-light to-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-authority-navy mb-4">
              Technical Expertise & Specializations
            </h2>
            <p className="text-xl text-neutral-gray max-w-2xl mx-auto">
              Comprehensive knowledge across all plumbing systems with high-level IPC code compliance expertise.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-6 shadow-lg border border-trust-primary/20 hover:shadow-xl transition-all duration-300">
              <div className="flex items-center mb-4">
                <Wrench className="w-8 h-8 text-trust-primary mr-3" />
                <h3 className="text-lg font-bold text-authority-navy">DWV Systems</h3>
              </div>
              <p className="text-neutral-gray mb-4">
                Expert in Drain, Waste, and Vent systems with comprehensive knowledge of residential and commercial applications.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-sm text-neutral-gray">
                  <CheckCircle className="w-4 h-4 text-success-primary mr-2" />
                  Underground Layout
                </li>
                <li className="flex items-center text-sm text-neutral-gray">
                  <CheckCircle className="w-4 h-4 text-success-primary mr-2" />
                  Pipe Sizing & Routing
                </li>
                <li className="flex items-center text-sm text-neutral-gray">
                  <CheckCircle className="w-4 h-4 text-success-primary mr-2" />
                  Code Compliance
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg border border-action-primary/20 hover:shadow-xl transition-all duration-300">
              <div className="flex items-center mb-4">
                <Home className="w-8 h-8 text-action-primary mr-3" />
                <h3 className="text-lg font-bold text-authority-navy">Water Systems</h3>
              </div>
              <p className="text-neutral-gray mb-4">
                Complete water system design, installation, and repair for residential and multifamily properties.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-sm text-neutral-gray">
                  <CheckCircle className="w-4 h-4 text-success-primary mr-2" />
                  Supply Line Installation
                </li>
                <li className="flex items-center text-sm text-neutral-gray">
                  <CheckCircle className="w-4 h-4 text-success-primary mr-2" />
                  Pressure Testing
                </li>
                <li className="flex items-center text-sm text-neutral-gray">
                  <CheckCircle className="w-4 h-4 text-success-primary mr-2" />
                  Fixture Installation
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg border border-action-urgent/20 hover:shadow-xl transition-all duration-300">
              <div className="flex items-center mb-4">
                <Zap className="w-8 h-8 text-action-urgent mr-3" />
                <h3 className="text-lg font-bold text-authority-navy">Gas Systems</h3>
              </div>
              <p className="text-neutral-gray mb-4">
                Licensed gas line installation and repair with focus on safety and code compliance.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-sm text-neutral-gray">
                  <CheckCircle className="w-4 h-4 text-success-primary mr-2" />
                  Gas Line Installation
                </li>
                <li className="flex items-center text-sm text-neutral-gray">
                  <CheckCircle className="w-4 h-4 text-success-primary mr-2" />
                  Appliance Connections
                </li>
                <li className="flex items-center text-sm text-neutral-gray">
                  <CheckCircle className="w-4 h-4 text-success-primary mr-2" />
                  Safety Inspections
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg border border-success-primary/20 hover:shadow-xl transition-all duration-300">
              <div className="flex items-center mb-4">
                <GraduationCap className="w-8 h-8 text-success-primary mr-3" />
                <h3 className="text-lg font-bold text-authority-navy">IPC Code Knowledge</h3>
              </div>
              <p className="text-neutral-gray mb-4">
                High-level International Plumbing Code knowledge ensuring all work meets or exceeds standards.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-sm text-neutral-gray">
                  <CheckCircle className="w-4 h-4 text-success-primary mr-2" />
                  Code Interpretation
                </li>
                <li className="flex items-center text-sm text-neutral-gray">
                  <CheckCircle className="w-4 h-4 text-success-primary mr-2" />
                  Permit Applications
                </li>
                <li className="flex items-center text-sm text-neutral-gray">
                  <CheckCircle className="w-4 h-4 text-success-primary mr-2" />
                  Inspection Preparation
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg border border-neutral-border hover:shadow-xl transition-all duration-300">
              <div className="flex items-center mb-4">
                <Building2 className="w-8 h-8 text-authority-navy mr-3" />
                <h3 className="text-lg font-bold text-authority-navy">Multifamily Expertise</h3>
              </div>
              <p className="text-neutral-gray mb-4">
                Specialized experience in large-scale multifamily residential projects from 50 to 400+ units.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-sm text-neutral-gray">
                  <CheckCircle className="w-4 h-4 text-success-primary mr-2" />
                  Project Management
                </li>
                <li className="flex items-center text-sm text-neutral-gray">
                  <CheckCircle className="w-4 h-4 text-success-primary mr-2" />
                  Crew Coordination
                </li>
                <li className="flex items-center text-sm text-neutral-gray">
                  <CheckCircle className="w-4 h-4 text-success-primary mr-2" />
                  Quality Control
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg border border-action-warm/20 hover:shadow-xl transition-all duration-300">
              <div className="flex items-center mb-4">
                <Trophy className="w-8 h-8 text-action-warm mr-3" />
                <h3 className="text-lg font-bold text-authority-navy">Fixture Specialists</h3>
              </div>
              <p className="text-neutral-gray mb-4">
                Complete fixture installation and repair expertise across residential and commercial applications.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-sm text-neutral-gray">
                  <CheckCircle className="w-4 h-4 text-success-primary mr-2" />
                  Water Heaters
                </li>
                <li className="flex items-center text-sm text-neutral-gray">
                  <CheckCircle className="w-4 h-4 text-success-primary mr-2" />
                  Toilets & Sinks
                </li>
                <li className="flex items-center text-sm text-neutral-gray">
                  <CheckCircle className="w-4 h-4 text-success-primary mr-2" />
                  Shower/Bath Systems
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Personal & Community Section */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-authority-navy mb-4">
              Personal & Community Involvement
            </h2>
            <p className="text-xl text-neutral-gray max-w-2xl mx-auto">
              Beyond plumbing expertise, Codee is deeply committed to family and community service in Highlands Ranch.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-success-light to-white rounded-xl p-8 border border-success-primary/20">
              <div className="flex items-center mb-6">
                <Heart className="w-8 h-8 text-success-primary mr-3" />
                <h3 className="text-xl font-bold text-authority-navy">Family Life</h3>
              </div>
              
              <ul className="space-y-4">
                <li className="flex items-start">
                  <Users className="w-5 h-5 text-success-primary mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-authority-navy">Loving Father</div>
                    <div className="text-neutral-gray">Father to Christian and Melodee</div>
                  </div>
                </li>
                <li className="flex items-start">
                  <Heart className="w-5 h-5 text-success-primary mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-authority-navy">Devoted Husband</div>
                    <div className="text-neutral-gray">Married to Michelle Jessen (business co-owner)</div>
                  </div>
                </li>
                <li className="flex items-start">
                  <Home className="w-5 h-5 text-success-primary mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-authority-navy">Family Business</div>
                    <div className="text-neutral-gray">Colorado First Plumbing is truly family-owned and operated</div>
                  </div>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-action-primary/10 to-white rounded-xl p-8 border border-action-primary/20">
              <div className="flex items-center mb-6">
                <Trophy className="w-8 h-8 text-action-primary mr-3" />
                <h3 className="text-xl font-bold text-authority-navy">Community Service</h3>
              </div>
              
              <ul className="space-y-4">
                <li className="flex items-start">
                  <Users className="w-5 h-5 text-action-primary mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-authority-navy">Volunteer Soccer Coach</div>
                    <div className="text-neutral-gray">Active youth soccer coach in Highlands Ranch community</div>
                  </div>
                </li>
                <li className="flex items-start">
                  <MapPin className="w-5 h-5 text-action-primary mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-authority-navy">Highlands Ranch Resident</div>
                    <div className="text-neutral-gray">Long-time community member and local business owner</div>
                  </div>
                </li>
                <li className="flex items-start">
                  <Shield className="w-5 h-5 text-action-primary mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-authority-navy">Community Leader</div>
                    <div className="text-neutral-gray">Committed to supporting local families and businesses</div>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Codee Section */}
      <section className="py-16 bg-gradient-to-br from-trust-deep to-authority-navy text-white">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Get Expert Service from a Master Plumber
          </h2>
          <p className="text-xl text-trust-light mb-8 max-w-2xl mx-auto">
            Work directly with Codee and the Colorado First Plumbing team for professional, reliable plumbing services backed by master-level expertise.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-2xl mx-auto">
            
            
          </div>
          
          {/* Emergency Notice */}
          <div className="mt-8 p-4 bg-action-urgent/20 rounded-xl border border-action-urgent/30 max-w-lg mx-auto">
            <div className="flex items-center justify-center mb-2">
              <Clock className="w-5 h-5 text-action-warm mr-2" />
              <span className="text-action-warm font-semibold">Emergency Service Available</span>
            </div>
            <p className="text-sm text-white/90">
              Master plumber-led emergency response available during business hours (8 AM - 5 PM Mon-Sat). Same-day service when possible.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CodeeJessen;